import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { HandCoins, ArrowRightLeft, Trash2, Banknote } from "lucide-react";
import { useAuth } from "../context/AuthContext";

function todayISO() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function formatINR(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

export default function CashAtCafeSection({ cashAtCafe, todaysCashSales, cashExpenses, totalHandovers, onChange }) {
  const { user } = useAuth();
  const [handovers, setHandovers] = useState([]);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const emptyForm = { amount: "", handed_to: "", note: "", date: todayISO() };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/cash-handovers", { params: { limit: 6 } });
      setHandovers(data);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDialog = () => {
    setForm({
      amount: cashAtCafe > 0 ? String(Math.round(cashAtCafe)) : "",
      handed_to: "",
      note: "",
      date: todayISO(),
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter a valid amount");
    if (!form.handed_to.trim()) return toast.error("Recipient name is required");
    setSubmitting(true);
    try {
      await api.post("/cash-handovers", { ...form, amount: Number(form.amount) });
      toast.success(`₹${Number(form.amount).toLocaleString("en-IN")} handed over to ${form.handed_to.trim()}`);
      setForm(emptyForm);
      setOpen(false);
      await load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to record handover");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this handover record? Cash at Cafe will increase by this amount.")) return;
    try {
      await api.delete(`/cash-handovers/${id}`);
      toast.success("Handover deleted");
      await load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete");
    }
  };

  const canDelete = (h) => user?.role === "admin" || h.created_by === user?.id;
  const positive = (cashAtCafe || 0) > 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8" data-testid="cash-at-cafe-section">
      {/* Left: hero card */}
      <div
        className={`lg:col-span-2 p-6 sm:p-8 relative overflow-hidden rounded-2xl shadow-sm ${
          positive
            ? "bg-espresso-800 text-white border border-espresso-900"
            : "bg-white border border-taupe-200"
        }`}
        data-testid="cash-at-cafe-hero"
      >
        <div className={`absolute -right-8 -top-8 w-40 h-40 rounded-full ${positive ? "bg-white/5" : "bg-taupe-100"}`} />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div className="flex-1">
            <div className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] mb-3 ${positive ? "text-white/70" : "text-taupe-500"}`}>
              <Banknote className="w-4 h-4" /> Cash at Cafe
            </div>
            <div
              className={`font-heading font-extrabold text-5xl sm:text-6xl tracking-tighter mb-2 ${
                positive ? "text-white" : "text-espresso-900"
              }`}
              data-testid="cash-at-cafe-amount"
            >
              {formatINR(cashAtCafe)}
            </div>
            <p className={`text-sm ${positive ? "text-white/70" : "text-taupe-600"}`}>
              Cash sales &minus; Cash expenses &minus; Handovers
            </p>
            <div className={`mt-4 flex flex-wrap gap-x-6 gap-y-2 text-xs ${positive ? "text-white/60" : "text-taupe-500"}`}>
              <span>Today&apos;s cash sales: <span className={positive ? "text-white font-semibold" : "text-espresso-900 font-semibold"}>{formatINR(todaysCashSales)}</span></span>
              <span>Cash expenses (all time): <span className={positive ? "text-white font-semibold" : "text-espresso-900 font-semibold"}>{formatINR(cashExpenses)}</span></span>
              <span>Handovers (all time): <span className={positive ? "text-white font-semibold" : "text-espresso-900 font-semibold"}>{formatINR(totalHandovers)}</span></span>
            </div>
          </div>

          <div className="flex sm:flex-col gap-2 sm:items-end">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  onClick={openDialog}
                  disabled={!positive}
                  className={`rounded-xl font-semibold h-12 px-6 ${
                    positive
                      ? "bg-white text-espresso-900 hover:bg-taupe-100"
                      : "bg-espresso-800 text-white hover:bg-espresso-900"
                  }`}
                  data-testid="handover-cash-button"
                >
                  <HandCoins className="w-4 h-4 mr-2" /> Handover cash
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md rounded-2xl">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl tracking-tight text-espresso-900">
                    Handover cash
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4" data-testid="handover-form">
                  <div className="bg-taupe-100 border border-taupe-200 rounded-xl p-4 text-sm">
                    <div className="text-taupe-600 mb-1">Current Cash at Cafe</div>
                    <div className="font-heading font-extrabold text-2xl text-espresso-900" data-testid="handover-dialog-balance">
                      {formatINR(cashAtCafe)}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Amount to handover (₹)</Label>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      required
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
                      data-testid="handover-amount-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Handed to</Label>
                    <Input
                      required
                      value={form.handed_to}
                      onChange={(e) => setForm({ ...form, handed_to: e.target.value })}
                      placeholder="e.g. Vinay"
                      className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
                      data-testid="handover-recipient-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Date</Label>
                    <Input
                      type="date"
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                      className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
                      data-testid="handover-date-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Note (optional)</Label>
                    <Textarea
                      rows={2}
                      value={form.note}
                      onChange={(e) => setForm({ ...form, note: e.target.value })}
                      placeholder="e.g. Evening deposit"
                      className="rounded-xl bg-taupe-100 border-taupe-200"
                      data-testid="handover-note-input"
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="rounded-xl bg-espresso-800 hover:bg-espresso-900 text-white font-semibold"
                      data-testid="submit-handover-button"
                    >
                      {submitting ? "Recording…" : "Record handover"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Right: recent handovers */}
      <div className="card-soft p-5" data-testid="recent-handovers-card">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-taupe-500 flex items-center gap-2">
            <ArrowRightLeft className="w-3.5 h-3.5" /> Recent handovers
          </div>
        </div>
        {handovers.length === 0 ? (
          <div className="text-sm text-taupe-500 py-8 text-center" data-testid="handovers-empty">
            No handovers yet.
          </div>
        ) : (
          <ul className="space-y-2 max-h-[240px] overflow-y-auto">
            {handovers.map((h) => (
              <li
                key={h.id}
                className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg hover:bg-taupe-100/50 transition-colors group"
                data-testid="handover-row"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-espresso-900 text-sm truncate">{h.handed_to}</div>
                  <div className="text-xs text-taupe-500">
                    {new Date(h.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                    {h.note ? ` · ${h.note}` : ""}
                  </div>
                </div>
                <div className="font-heading font-bold text-espresso-900 text-sm shrink-0">
                  {formatINR(h.amount)}
                </div>
                <button
                  onClick={() => remove(h.id)}
                  disabled={!canDelete(h)}
                  className="p-1.5 rounded-md text-terracotta-500 opacity-0 group-hover:opacity-100 hover:bg-terracotta-100 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid={`delete-handover-${h.id}`}
                  aria-label="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
